import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CacheableScene } from '@/data/editorDataLayer/internal/textAtomCacheManager';
import { calculateOrderBetween, findSceneTextAtomQueries } from '@/hooks/optimisticHelpers';
import type { CreateTextAtom, IEntityTag, TextAtoms } from '@/types';
import { api } from './internal/endpoints';
import { optimisticToRealIdMap } from './internal/optimisticIdMap';
import { rollbackTextAtomCacheUpdate, withTextAtomCacheUpdate } from './internal/textAtomCacheUpdaters';

export const useCreateTextAtom = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: {
            projectId: number;
            data: CreateTextAtom;
            optimisticId?: number;
            characterData?: { entity_tag: IEntityTag } | null;
            uiPreviousId?: number | null;
        }) => {
            return api.createTextAtom(params.projectId, params.data);
        },
        onMutate: async params => {
            const optimisticId = params.optimisticId || Date.now();

            const { previousData } = await withTextAtomCacheUpdate(queryClient, (scene: CacheableScene) => {
                if (scene.id === params.data.textable_id && params.data.textable_model === 'scene') {
                    const textAtoms = scene.text_atoms || [];

                    const previousIdForOrder = params.uiPreviousId || params.data.previous_id;
                    const previousAtom = previousIdForOrder
                        ? textAtoms.find((atom: TextAtoms) => atom.id === previousIdForOrder) || null
                        : null;
                    const nextAtom = params.data.next_id
                        ? textAtoms.find((atom: TextAtoms) => atom.id === params.data.next_id) || null
                        : null;

                    const calculatedOrder = params.data.order || calculateOrderBetween(previousAtom, nextAtom);

                    let insertIndex = textAtoms.length;
                    const previousIdForPosition = params.uiPreviousId || params.data.previous_id;
                    if (previousIdForPosition) {
                        const prevIndex = textAtoms.findIndex((atom: TextAtoms) => atom.id === previousIdForPosition);
                        if (prevIndex >= 0) {
                            insertIndex = prevIndex + 1;
                        }
                    }

                    const optimisticAtom: TextAtoms = {
                        id: optimisticId,
                        content: params.data.content || '',
                        content_json: params.data.content_json || undefined,
                        text_atom_type_id: params.data.text_atom_type_id,
                        tags: params.characterData
                            ? [
                                  {
                                      ...params.characterData.entity_tag,
                                      category: { id: 0, name: '', target_type: '', target_id: 0 },
                                      subject_type: 'text_atom',
                                      subject_id: optimisticId,
                                      subject_model: 'TextAtom',
                                  },
                              ]
                            : [],
                        project_id: params.projectId,
                        order: calculatedOrder,
                        textable_id: params.data.textable_id,
                        textable_type: params.data.textable_model,
                    };

                    const newTextAtoms = [...textAtoms];
                    newTextAtoms.splice(insertIndex, 0, optimisticAtom);

                    return { ...scene, text_atoms: newTextAtoms };
                }
                return scene;
            });

            return { previousData, optimisticId };
        },
        onError: (_err, _params, context) => {
            if (context?.previousData) {
                rollbackTextAtomCacheUpdate(queryClient, context.previousData);
            }
        },
        onSuccess: async (result, _params, context) => {
            if (result.success && result.data) {
                const optimisticId = context?.optimisticId;
                const realId = result.data.id;

                if (optimisticId && realId) {
                    optimisticToRealIdMap.set(optimisticId, realId);

                    const textAtomQueries = findSceneTextAtomQueries(queryClient);

                    for (const query of textAtomQueries) {
                        queryClient.setQueryData<CacheableScene[]>(query.queryKey, old => {
                            if (!old || !Array.isArray(old)) return old;

                            return old.map(scene => {
                                if (scene.text_atoms?.length) {
                                    return {
                                        ...scene,
                                        text_atoms: scene.text_atoms.map(atom =>
                                            atom.id === optimisticId ? { ...atom, ...result.data, id: realId } : atom,
                                        ),
                                    };
                                }
                                return scene;
                            });
                        });
                    }
                }
            }
        },
    });
};

export const useUpdateTextAtom = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: { projectId: number; atomId: number; data: Partial<CreateTextAtom> }) => {
            return api.updateTextAtom(params.projectId, params.atomId, params.data);
        },
        onMutate: async params => {
            const { previousData } = await withTextAtomCacheUpdate(queryClient, (scene: CacheableScene) => {
                if (scene.text_atoms?.length) {
                    return {
                        ...scene,
                        text_atoms: scene.text_atoms.map(atom => {
                            if (atom.id !== params.atomId) return atom;
                            return {
                                ...atom,
                                ...(params.data.content !== undefined && { content: params.data.content }),
                                ...(params.data.content_json !== undefined && { content_json: params.data.content_json ?? undefined }),
                                ...(params.data.order !== undefined && { order: params.data.order }),
                                ...(params.data.text_atom_type_id !== undefined && { text_atom_type_id: params.data.text_atom_type_id }),
                            };
                        }),
                    };
                }
                return scene;
            });

            return { previousData };
        },
        onError: (_err, _params, context) => {
            if (context?.previousData) {
                rollbackTextAtomCacheUpdate(queryClient, context.previousData);
            }
        },
    });
};

export const useDeleteTextAtom = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: { projectId: number; atomId: number }) => {
            return api.deleteTextAtom(params.projectId, params.atomId);
        },
        onMutate: async params => {
            const { previousData } = await withTextAtomCacheUpdate(queryClient, (scene: CacheableScene) => {
                if (scene.text_atoms?.length) {
                    return {
                        ...scene,
                        text_atoms: scene.text_atoms.filter(atom => atom.id !== params.atomId),
                    };
                }
                return scene;
            });

            return { previousData };
        },
        onError: (_err, _params, context) => {
            if (context?.previousData) {
                rollbackTextAtomCacheUpdate(queryClient, context.previousData);
            }
        },
    });
};
